import { deepMerge } from './deepMerge';
function coerce<T>(el: HTMLElement, val: T): T;
function coerce(el: HTMLElement, val: string): number | string;
function coerce(el: HTMLElement, val: unknown): unknown {
  if (el.dataset['coerce'] === 'false') {
    return val;
  }

  if (typeof val === 'string') {
    const n = Number(val);
    if (Number.isNaN(n) || !Number.isFinite(n) || !/^\d+$/.test(val)) {
      return val;
    }

    return n;
  }

  return val;
}

class ElementBasedError extends Error {
  public override name = 'ElementBasedError';

  public constructor(
    message: string,
    public readonly el: Element,
  ) {
    super(message);
  }
}

class SerializationError extends ElementBasedError {
  public override name = 'SerializationError';
  public constructor(message: string, el: Element) {
    super(message, el);
  }
}

const patterns = {
  key: /[\w-]+|(?=\[])/gi,
  named: /^[\w-]+$/i,
  push: /^$/,
  validate: /^[a-z][\w-]*(?:\[(?:\d*|[\w-]+)])*$/i,
};

function serializeInput(
  el: HTMLElement,
): (number | string)[] | Date | number[] | string[] | boolean | number | string | null | undefined {
  const tag = el.tagName.toLowerCase();
  switch (tag) {
    case 'textarea': {
      return (el as HTMLTextAreaElement).value;
    }

    case 'select': {
      const sel = el as HTMLSelectElement;
      if (sel.multiple) {
        const out: (number | string)[] = [];
        for (const ch of Array.from(sel.childNodes as NodeListOf<HTMLOptionElement>)) {
          if (ch.selected) {
            out.push(coerce(sel, ch.value));
          }
        }

        return coerce(sel, out);
      }

      return coerce(sel, sel.value);
    }

    case 'input': {
      const i = el as HTMLInputElement;
      const type = el.getAttribute('type');
      switch (type) {
        case 'radio': {
          return coerce(i, i.checked ? (i.getAttribute('value') ? i.value : true) : undefined);
        }

        case 'checkbox': {
          return coerce(
            i,
            i.checked
              ? i.getAttribute('value')
                ? i.value
                : true
              : i.getAttribute('value')
                ? undefined
                : false,
          );
        }

        case 'range':
        case 'number': {
          const v = i.valueAsNumber;
          /*
           * Browser that don't really support `valueAsNumber`
           * (Edge) https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/669685/
           */
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (v === undefined || Number.isNaN(v)) {
            return Number.parseFloat(i.value);
          }

          return i.valueAsNumber;
        }

        case 'date':
        case 'datetime-local': {
          // fallback for ios and such, enjoy your shit
          return i.valueAsDate ? i.valueAsDate.toJSON() : new Date(i.value).toJSON();
        }

        case 'tel': {
          // eslint-disable-next-line unicorn/prefer-string-replace-all
          return i.value.replace(/[^\d+]/gi, '');
        }

        default: {
          return i.value;
        }
      }
    }
  }

  throw new SerializationError('Could not serialize element', el);
}

const shouldIgnore = (value: unknown) => value === undefined || value === false || value === null;

export function serializeFormJSON(form: HTMLFormElement): object {
  const json = {};

  for (const el of Array.from(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    form.querySelectorAll(
      'input[name], textarea[name], select[name]',
    ) as NodeListOf<HTMLInputElement>,
  )) {
    let name = el.getAttribute('name')!;

    let value = serializeInput(el);

    if (value === '') {
      if (el.required) {
        continue;
      }

      value = null;
    }

    if (name.endsWith('?')) {
      if (value === null) {
        continue;
      }

      name = name.slice(0, -1);
    }

    // skip invalid keys
    if (!patterns.validate.test(name)) {
      continue;
    }

    let k: string | undefined;
    const keys = name.match(patterns.key);
    let merge: unknown = value;
    let reverseKey = name;
    if (!keys) {
      throw new SerializationError('Could not match keys', el);
    }

    while ((k = keys.pop()) !== undefined) {
      // adjust reverse_key
      reverseKey = reverseKey.replace(new RegExp(`\\[${k}\\]$`), '');

      // push
      if (patterns.push.test(k)) {
        merge = shouldIgnore(merge) ? [] : [merge];
      } else if (patterns.named.test(k)) {
        // named
        merge = { [k]: merge };
      }
    }

    deepMerge(json, merge as object);
  }

  return json;
}
